import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { createPayment } from "../../services/paymentService"
import { fr } from "../../locales/fr"
import { Button } from "../ui/Button"
import { Input } from "../ui/Input"
import { Label } from "../ui/Label"
import { Select } from "../ui/Select"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/Card"

function defaultSubscriptionId(subs) {
  if (!subs?.length) return null
  const active = subs.filter((s) => s.status === "active")
  if (active.length === 1) return active[0].id
  if (subs.length === 1) return subs[0].id
  if (active.length > 0) return active[0].id
  return subs[0].id
}

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   memberId: number,
 *   subscriptions?: Array<{ id: number, pack?: string, plan?: string, status?: string }>,
 *   onSuccess?: () => void,
 * }} props
 */
export function PaymentModal({ open, onClose, memberId, subscriptions = [], onSuccess }) {
  const d = fr.memberDetail
  const [amount, setAmount] = useState("")
  const [method, setMethod] = useState("cash")
  const [subscriptionId, setSubscriptionId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (open) {
      setAmount("")
      setMethod("cash")
      setError("")
      setSubscriptionId(defaultSubscriptionId(subscriptions))
    }
  }, [open, subscriptions])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])

  const submit = async () => {
    setError("")
    const parsed = parseFloat(String(amount).replace(",", "."), 10)
    if (!parsed || parsed <= 0) {
      setError(d.paymentInvalidAmount)
      return
    }
    setSubmitting(true)
    try {
      await createPayment({
        member_id: memberId,
        amount: parsed,
        method,
        ...(subscriptionId != null ? { subscription_id: subscriptionId } : {}),
      })
      onSuccess?.()
      onClose()
    } catch {
      setError(d.paymentError)
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  const modal = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        aria-label={fr.common.close}
        onClick={onClose}
      />
      <Card className="relative z-10 w-full max-w-md border-border shadow-lg">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
          <div className="space-y-1.5">
            <CardTitle className="text-lg">{d.paymentCardTitle}</CardTitle>
            <CardDescription>{d.paymentCardHint}</CardDescription>
          </div>
          <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={onClose} aria-label={fr.common.cancel}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="payment-modal-amount">{d.paymentAmount}</Label>
              <Input
                id="payment-modal-amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="payment-modal-method">{d.paymentMethod}</Label>
              <Select id="payment-modal-method" value={method} onChange={(e) => setMethod(e.target.value)}>
                <option value="cash">{fr.paymentMethod.cash}</option>
                <option value="card">{fr.paymentMethod.card}</option>
                <option value="transfer">{fr.paymentMethod.transfer}</option>
              </Select>
            </div>
            {subscriptions.length > 0 ? (
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="payment-modal-sub">{d.paymentSubscriptionLabel}</Label>
                <Select
                  id="payment-modal-sub"
                  value={subscriptionId ?? ""}
                  onChange={(e) => {
                    const v = e.target.value
                    setSubscriptionId(v === "" ? null : Number(v))
                  }}
                >
                  {subscriptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {[s.pack, s.plan].filter(Boolean).join(" — ") || `#${s.id}`} ({s.status})
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-muted-foreground">{d.paymentSubscriptionHint}</p>
              </div>
            ) : null}
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {fr.common.cancel}
            </Button>
            <Button type="button" onClick={submit} disabled={submitting}>
              {submitting ? fr.common.saving : d.paymentSubmit}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )

  return createPortal(modal, document.body)
}
